import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function DevToolingWorkflow() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "The Terminal-First Dev Workflow: CLI Tools, AI Agents & MCP",
        "July 23, 2026",
        "6min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                This is the companion to the push-notifications post: the actual toolchain behind
                the build. I&apos;m a terminal-first developer, so almost everything runs through
                the command line — package managers, Git, deployment, AI coding agents, and a small
                fleet of MCP servers that glue it together. Here&apos;s the full inventory, minus
                anything that would be a security risk to share.
            </p>

            <h2 className="post-sect">Runtime and version managers</h2>
            <p>
                Two tools keep the runtimes in check.{" "}
                <code>mise</code> manages every language runtime — Ruby versions per project live in{" "}
                <code>~/.config/mise/config.toml</code> (Ruby 3.4.8 powers the Rails apps, with
                3.3.0 around for legacy projects). <code>fnm</code> handles Node, currently on
                v20.20.1 as default. Both respect a project file (<code>.mise.toml</code> /{" "}
                <code>.nvmrc</code>) so a repo pins its own stack.
            </p>
            <pre>
                <code className="language-bash">{`$ mise ls          # ruby 3.3.0, ruby 3.4.8
$ fnm ls           # * v20.20.1 default, system`}</code>
            </pre>

            <h2 className="post-sect">Core CLI tools</h2>
            <ul className="lead">
                <li><strong>git</strong> — obviously. SSH-based remotes, so no token in the repo URL.</li>
                <li><strong>gh (GitHub CLI)</strong> — auth, PRs, issues, and the credential helper. The global git config routes HTTPS Git credentials through <code>gh auth git-credential</code>, so there&apos;s exactly one auth path for GitHub.</li>
                <li><strong>vercel</strong> — deploys the blog. One <code>vercel</code> command from the repo root; production branch <code>main</code> is watched, and build hooks/CLI handle the rest.</li>
                <li><strong>docker</strong> — local infrastructure (Postgres, MinIO) via compose files instead of installing services on the host.</li>
                <li><strong>jq</strong> — JSON wrangling in pipes, e.g. extracting fields from API responses or config files.</li>
                <li><strong>ruby / rails</strong> — provided by mise, on PATH for every project.</li>
            </ul>

            <h2 className="post-sect">AI coding agents</h2>
            <p>
                The terminal is also where the AI lives. Two agents do the heavy lifting:
            </p>
            <h3 className="post-sect-sub">opencode</h3>
            <p>
                opencode is the primary agent — it edits files, runs commands, searches the
                codebase, and manages the whole task lifecycle from the shell. Its config at{" "}
                <code>~/.opencode/opencode.json</code> defines a local model provider (Ollama on{" "}
                <code>localhost:11434</code>) for offline work, plus an MCP server for web search
                (below). Skills and sub-agents let it specialize: one agent explores the codebase,
                another executes multi-step tasks.
            </p>
            <h3 className="post-sect-sub">Codex</h3>
            <p>
                Codex handles cloud-model work and the TiiRADA mobile app. Its{" "}
                <code>~/.codex/config.toml</code> pins a model and reasoning effort, and it defines
                an Ollama-backed provider profile for local, offline launches. It also has a voice
                transcription feature enabled — handy for dictating this very post.
            </p>
            <pre>
                <code className="language-toml">{`# ~/.codex/config.toml (structure, values redacted)
model = "gpt-5.x"
model_reasoning_effort = "low"

[projects."/home/.../TiiRADA/tiirada-fe"]
trust_level = "trusted"

[profiles.ollama-launch]
openai_base_url = "http://127.0.0.1:11434/v1/"
model_provider = "ollama-launch"

[features]
voice_transcription = true`}</code>
            </pre>

            <h2 className="post-sect">MCP servers</h2>
            <p>
                MCP (Model Context Protocol) is how agents get live data without me pasting it in.
                Each server is a small process the agent spawns and talks to over stdio. Two are
                wired up:
            </p>
            <ul className="lead">
                <li><strong>tavily-search</strong> — a local MCP server (<code>npx tavily-mcp</code>) giving agents web search, URL extraction, site mapping, and deep research. The API key lives in the opencode config, environment-scoped to that one server — never in code.</li>
                <li><strong>Ollama provider</strong> — not an MCP server, but the same idea: a local model endpoint agents can fall back to when you don&apos;t want queries leaving the machine.</li>
            </ul>
            <pre>
                <code className="language-json">{`// ~/.opencode/opencode.json (structure, key redacted)
{
  "mcp": {
    "tavily-search": {
      "type": "local",
      "command": ["npx", "-y", "tavily-mcp@latest"],
      "environment": {
        "TAVILY_API_KEY": "<redacted, env-scoped>"
      },
      "enabled": true
    }
  }
}`}</code>
            </pre>

            <h2 className="post-sect">Agent workflows</h2>
            <p>
                Repetitive tasks are codified as reusable workflows in the{" "}
                <code>.agent/workflows/</code> directory of each repo — a YAML/declarative file
                with a description and steps. The blog has one for adding a movie to the Cinematic
                Vault: append the entry, run a thumbnail-check script, re-check until every poster
                passes. The agent reads the workflow and executes it start to finish instead of me
                re-typing the ritual every time.
            </p>
            <pre>
                <code className="language-markdown">{`# .agent/workflows/add-movie.md (excerpt)
---
description: Add a movie to the Cinematic Vault and verify thumbnails
---

1. Add the new movie entry to \`src/data/mediaData.js\`.
2. Run \`node scripts/check_thumbnails.js\`.
3. If any thumbnails fail, find a replacement URL and update.
4. Re-run until all entries pass.`}</code>
            </pre>

            <h2 className="post-sect">Security rules I actually follow</h2>
            <ul className="lead">
                <li><strong>Secrets live in env vars or secret managers</strong> — never in a repo. API keys in agent configs are scoped to a single server process.</li>
                <li><strong>GitHub auth goes through one helper</strong> (<code>gh auth git-credential</code>), so no passwords or tokens get pasted into URLs.</li>
                <li><strong>Service account keys</strong> for cloud APIs (like the FCM key from the push post) are loaded from a local file path or <code>GOOGLE_APPLICATION_CREDENTIALS</code>, not committed — and blog posts redact them.</li>
                <li><strong>Local services run in Docker</strong>, keeping the host clean and the blast radius small.</li>
            </ul>

            <h2 className="post-sect">Summary</h2>
            <p>
                mise + fnm manage runtimes, git + gh + vercel handle the plumbing, docker runs
                local infra, and opencode + Codex do the heavy lifting with Ollama as an offline
                fallback. MCP servers bring in live data on demand, and agent workflows turn
                recurring chores into one-command rituals — all from a terminal, with secrets
                locked to env-scoped configs.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}